import * as React from 'react';
// @ts-expect-error
import {components, ControlProps, OptionProps} from 'react-select';

import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {PeepTwoLine} from 'shells/peep/peep-two-line';
import type {CreateSelectSize} from 'shells/select-inline/select-inline-creatable';

import {ControlWithIcon} from './control-with-icon';
// @ts-expect-error
import {GraphQLSelectCreatable, Option} from './graphql-select-creatable';
import {useCreateContact} from './hooks/use-create-contact';
import {useGetContacts} from './hooks/use-get-contacts';

type Props = {
    selectedValues: Option[];
    onChange: (values: Option | Option[]) => any;
    onClearValue?: () => void;
    isMulti: boolean;
    clearable?: boolean;
    noIcon?: boolean;
    size?: CreateSelectSize;
    lightPlaceholderText?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    formatCreateLabel?: (inputValue: string) => string;
    persistTextOnBlur?: boolean;
    onCreate?: (newValue: string) => void;
};

export const GraphQLSelectContacts = (props: Props) => {
    const {
        noIcon,
        size = 'normal',
        formatCreateLabel = (inputValue) => `Create new person named '${inputValue}'`,
    } = props;
    const {fetchMore, isLoading} = useGetContacts();
    const createContact = useCreateContact();

    const handleCreate = (newContact: string) => {
        // Immediately add a temp contact to the list of selected values
        const tempContact = {id: newContact, name: newContact} as const;
        if (props.isMulti && Array.isArray(props.selectedValues)) {
            props.onChange([...props.selectedValues, tempContact]);
        } else {
            props.onChange(tempContact);
        }

        createContact({
            contact: {name: newContact},
        }).then((result) => {
            const createdContact =
                result.data && result.data.contactCreate && result.data.contactCreate.contact;
            const createdContactValue = createdContact
                ? {id: createdContact.id, name: createdContact.name}
                : null;

            // Add the real created contact to the list of selected values
            if (createdContactValue) {
                if (props.isMulti && Array.isArray(props.selectedValues)) {
                    props.onChange([...props.selectedValues, createdContactValue]);
                } else {
                    props.onChange(createdContactValue);
                }
            }
        });
    };

    const handleLoadOptions = (search?: string | null) => {
        return fetchMore(search).then(({data}) => {
            const quickResults = data.search ? data.search.quickResults : null;

            let contactResults = quickResults ? quickResults.contactResults : [];

            if (contactResults.length === 0) {
                const recentResults =
                    quickResults && quickResults.recentResults ? quickResults.recentResults : [];
                if (recentResults && recentResults.length > 0) {
                    contactResults = recentResults;
                }
            }

            return Promise.resolve(contactResults);
        });
    };

    const ControlComponent = React.useMemo(
        () =>
            ({children, ...controlProps}: ControlProps) => (
                <components.Control {...controlProps}>
                    {noIcon ? (
                        children
                    ) : (
                        <ControlWithIcon icon={ContactIcon}>{children}</ControlWithIcon>
                    )}
                </components.Control>
            ),
        [noIcon]
    );

    const OptionComponent = React.useMemo(
        () => (optionProps: OptionProps) => {
            // This is a hack to get around limitations in our old version of flow
            const spreadableProps: OptionProps = optionProps as any;

            if (optionProps.data.__isNew__) {
                return (
                    <components.Option {...spreadableProps}>
                        {optionProps.data.name}
                    </components.Option>
                );
            }

            return (
                <components.Option {...spreadableProps}>
                    <PeepTwoLine
                        name={optionProps.data.name}
                        avatarUrl={optionProps.data.avatarUrl}
                        initials={optionProps.data.initials}
                        htmlUrl={optionProps.data.htmlUrl}
                        type='contacts'
                        relatedEntityName={
                            optionProps.data.accounts &&
                            optionProps.data.accounts.edges &&
                            optionProps.data.accounts.edges[0]
                                ? optionProps.data.accounts.edges[0].node.name
                                : undefined
                        }
                        isLinkable={false}
                        size={size}
                    />
                </components.Option>
            );
        },
        [size]
    );

    const placeholder =
        props.placeholder ||
        (props.isMulti ? 'Select or create people' : 'Select or create a person');

    return (
        <GraphQLSelectCreatable
            placeholder={placeholder}
            value={props.selectedValues}
            multi={props.isMulti}
            onClearValue={props.onClearValue}
            loadOptions={handleLoadOptions}
            onChange={props.onChange}
            onBlur={() => {
                props.onChange(props.selectedValues);
            }}
            noOptionsMessage={({inputValue}) => {
                if (isLoading) {
                    return 'Getting recent results…';
                }
                if (inputValue.length) {
                    return `No people found matching '${inputValue}'`;
                }
                if (inputValue.length === 0) {
                    return 'No recently viewed people. Type to search all people…';
                }

                return null;
            }}
            // @ts-expect-error
            formatCreateLabel={formatCreateLabel}
            onCreate={props.onCreate ? props.onCreate : handleCreate}
            components={{Control: ControlComponent, Option: OptionComponent}}
            labelKey='name'
            valueKey='id'
            clearable={props.clearable}
            size={props.size}
            lightPlaceholderText={props.lightPlaceholderText}
            autoFocus={props.autoFocus}
            persistTextOnBlur={props.persistTextOnBlur}
        />
    );
};
